import React, { useState, useEffect, useRef } from 'react';
import { useAuthContext } from '../../context/AuthContext';

const IdleTimeoutWarning = () => {
  const { currentUser, logout } = useAuthContext();
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(60); // 60 secondes d'avertissement
  const warningTime = 60000; // 60 secondes avant déconnexion
  const warningTimeoutRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const logoutTimeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // Fonction pour détecter l'activité utilisateur
  const handleUserActivity = () => {
    if (!showWarning) {
      lastActivityRef.current = Date.now();
    }
  };

  // Réinitialiser l'alerte quand l'utilisateur se déconnecte
  useEffect(() => {
    // Si l'utilisateur n'est plus connecté, cacher l'alerte
    if (!currentUser && showWarning) {
      console.log("Utilisateur déconnecté, fermeture de l'alerte d'inactivité");
      setShowWarning(false);
      setCountdown(60);
      clearTimeout(warningTimeoutRef.current);
      clearInterval(countdownIntervalRef.current);
      clearTimeout(logoutTimeoutRef.current);
    }
  }, [currentUser, showWarning]);

  // Attacher les événements de détection d'activité
  useEffect(() => {
    if (!currentUser) return;

    // Liste des événements à surveiller pour tous les appareils
    const events = [
      'mousedown', 'mousemove', 'keydown', 'wheel', 'DOMMouseScroll', 'mousewheel',
      'touchstart', 'touchmove', 'touchend', 'click', 'visibilitychange'
    ];
    
    // Ajouter les écouteurs d'événements
    events.forEach(event => {
      window.addEventListener(event, handleUserActivity, { passive: true });
    });
    
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleUserActivity);
      });
    };
  }, [currentUser]);

  // Effectuer le compte à rebours et la déconnexion
  useEffect(() => {
    // Si le warning est affiché, démarrer le compte à rebours et programmer la déconnexion
    if (showWarning && currentUser) {
      console.log("Démarrage du compte à rebours de 60 secondes");
      
      // Démarrer le compte à rebours
      setCountdown(60);
      
      // Nettoyer tout intervalle existant pour éviter les doublons
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
      
      // Configurer un nouvel intervalle de compte à rebours
      countdownIntervalRef.current = setInterval(() => {
        setCountdown(prevCount => {
          const newValue = prevCount - 1;
          console.log("Compte à rebours:", newValue);
          
          // Si le compte à rebours atteint zéro, déclencher la déconnexion
          if (newValue <= 0) {
            clearInterval(countdownIntervalRef.current);
            console.log("Compte à rebours terminé, déconnexion imminente");
            setShowWarning(false);
            logout();
            return 0;
          }
          return newValue;
        });
      }, 1000);
      
      // Programmer également la déconnexion après le délai complet comme sauvegarde
      logoutTimeoutRef.current = setTimeout(() => {
        console.log("Délai d'avertissement écoulé, déconnexion forcée");
        clearInterval(countdownIntervalRef.current);
        setShowWarning(false);
        logout();
      }, warningTime + 2000); // 2 secondes supplémentaires pour s'assurer que le compte à rebours a une chance de se terminer
    }
    
    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
      if (logoutTimeoutRef.current) {
        clearTimeout(logoutTimeoutRef.current);
      }
    };
  }, [showWarning, currentUser, logout]);

  // Gérer l'affichage de l'avertissement basé sur l'inactivité
  useEffect(() => {
    if (!currentUser) return;

    console.log("Configuration du timer d'avertissement d'inactivité");
    
    // Configuration du timer principal (14min avant avertissement)
    warningTimeoutRef.current = setTimeout(() => {
      console.log("Affichage de l'avertissement d'inactivité après 14 minutes d'inactivité");
      setShowWarning(true);
    }, 840000); // 14 minutes (840000ms)
    
    // Vérification supplémentaire pour les appareils mobiles
    const mobileCheckInterval = setInterval(() => {
      const now = Date.now();
      const timeSinceLastActivity = now - lastActivityRef.current;
      
      // Si plus de 14 minutes se sont écoulées sans activité et que l'avertissement n'est pas affiché
      if (timeSinceLastActivity >= 840000 && !showWarning && currentUser) {
        console.log("Longue inactivité détectée via l'intervalle de vérification: ", timeSinceLastActivity);
        clearTimeout(warningTimeoutRef.current);
        setShowWarning(true);
      }
    }, 10000); // Vérifier toutes les 10 secondes
    
    return () => {
      clearTimeout(warningTimeoutRef.current);
      clearInterval(mobileCheckInterval);
    };
  }, [currentUser, showWarning]);
  
  // Réinitialiser le timer quand l'utilisateur clique sur "Je suis toujours là"
  const handleStillHere = () => {
    console.log("L'utilisateur a cliqué sur 'Je suis toujours là'");
    
    // Nettoyer les timers existants
    clearTimeout(logoutTimeoutRef.current);
    clearInterval(countdownIntervalRef.current);
    clearTimeout(warningTimeoutRef.current);
    
    // Cacher l'avertissement
    setShowWarning(false);
    
    // Réinitialiser le timestamp de dernière activité
    lastActivityRef.current = Date.now();
    
    // Réinitialiser le timer principal
    warningTimeoutRef.current = setTimeout(() => {
      setShowWarning(true);
    }, 840000); // Remise à 14 minutes
  };
  
  // Si l'avertissement n'est pas affiché ou l'utilisateur n'est pas connecté, ne rien afficher
  if (!showWarning || !currentUser) return null;
  
  return (
    <div className="fixed bottom-0 inset-x-0 p-2 sm:p-4 flex justify-center z-50">
      <div className="bg-warning text-warning-content px-4 py-3 sm:px-6 sm:py-4 rounded-lg shadow-lg flex flex-col sm:flex-row sm:items-center sm:justify-between max-w-lg w-full">
        <div className="mb-2 sm:mb-0">
          <h4 className="font-bold text-sm sm:text-base">Déconnexion imminente</h4>
          <p className="text-xs sm:text-sm">Vous serez déconnecté pour inactivité dans {countdown} secondes.</p>
        </div>
        <button 
          onClick={handleStillHere}
          className="btn btn-sm btn-ghost mt-1 sm:mt-0 sm:ml-2 self-end sm:self-auto"
        >
          Je suis toujours là
        </button>
      </div>
    </div>
  );
};

export default IdleTimeoutWarning;